import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { updateManifest } from '../utils/manifestGenerator';

type ThemeMode = 'default' | 'dark' | 'light';

interface ThemeContextType {
    themeMode: ThemeMode;
    isDarkMode: boolean;
    setThemeMode: (mode: ThemeMode) => void;
    cycleThemeMode: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

interface ThemeProviderProps {
    children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
    const [themeMode, setThemeModeState] = useState<ThemeMode>(() => {
        // Check localStorage for saved preference
        const saved = localStorage.getItem('themeMode');
        if (saved === 'dark' || saved === 'light' || saved === 'default') {
            return saved as ThemeMode;
        }
        // Default to 'default' if no preference is saved
        return 'default';
    });

    // Determine if dark mode should be active based on theme mode and system preference
    const isDarkMode = (() => {
        switch (themeMode) {
            case 'dark':
                return true;
            case 'light':
                return false;
            case 'default':
                return window.matchMedia('(prefers-color-scheme: dark)').matches;
            default:
                return false;
        }
    })();

    const setThemeMode = (mode: ThemeMode) => {
        setThemeModeState(mode);
        localStorage.setItem('themeMode', mode);
    };

    const cycleThemeMode = () => {
        const modes: ThemeMode[] = ['default', 'light', 'dark'];
        const currentIndex = modes.indexOf(themeMode);
        const nextIndex = (currentIndex + 1) % modes.length;
        setThemeMode(modes[nextIndex]);
    };

    // Apply theme to document and update manifest
    useEffect(() => {
        if (isDarkMode) {
            document.documentElement.setAttribute('data-theme', 'dark');
        } else {
            document.documentElement.setAttribute('data-theme', 'light');
        }

        // Update manifest with current theme colors
        updateManifest(isDarkMode);
    }, [isDarkMode]);

    // Listen for system theme changes when in 'default' mode
    useEffect(() => {
        if (themeMode !== 'default') return;

        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = () => {
            // Force re-render by updating localStorage (this will trigger the effect above)
            localStorage.setItem('themeMode', 'default');
        };

        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, [themeMode]);

    // Add keyboard shortcut for cycling theme modes (Ctrl/Cmd + D)
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if ((event.ctrlKey || event.metaKey) && event.key === 'd') {
                event.preventDefault();
                cycleThemeMode();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [cycleThemeMode]);

    // Cleanup blob URLs on unmount
    useEffect(() => {
        return () => {
            const manifestLink = document.querySelector('link[rel="manifest"]') as HTMLLinkElement;
            if (manifestLink && manifestLink.href.startsWith('blob:')) {
                URL.revokeObjectURL(manifestLink.href);
            }
        };
    }, []);

    const value = {
        themeMode,
        isDarkMode,
        setThemeMode,
        cycleThemeMode
    };

    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    );
}; 